import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { deleteLeadContactData } from 'containers/Leads/Lead/actions';
import InputField from 'components/FormFields/InputField';
import ContactField, { AddressContactFields } from 'components/ContactField/ContactField';
import { CONTACT_FIELD_LABEL_INFO } from 'components/ContactField/ContactFieldType';
import LeadRecordAdditonalContact from './LeadRecordAdditionalContact';
import { validateLeadUpdate } from './utils/leadValidation';

const LeadRecordContactInfo = ({
  orgId,
  leadId,
  contact,
  serviceTypes,
  sourceTypes,
  contactCompanyName,
  role,
  emails = [],
  phones = [],
  addresses = [],
  websites = [],
  updateLeadFields,
}) => {
  const dispatch = useDispatch();

  const [primaryAddress, ...addtAddresses] = addresses;
  const [primaryWebsite, ...addtWebsites] = websites;
  /* eslint-disable no-unused-vars */
  const [primaryEmail, ...restEmails] = emails;
  const [primaryPhone, ...restPhones] = phones;
  /* eslint-enable no-unused-vars */

  const [createNewPhone, setNewPhone] = useState(false);
  const [createNewEmail, setNewEmail] = useState(false);
  const [createNewWebsite, setNewWebsite] = useState(false);
  const [createNewAddress, setNewAddress] = useState(false);

  const hasAddtionalContact = useMemo(() =>
    [...emails, ...phones, ...addtAddresses, ...addtWebsites].length > 0,
    [emails, phones, addtAddresses, addtWebsites],
  );
  const isCreating = useMemo(() => {
    return createNewPhone || createNewEmail || createNewWebsite || createNewAddress;
  }, [createNewPhone, createNewEmail, createNewWebsite, createNewAddress]);

  const handleEmailUpdate = (index, validatedData) => {
    const isNew = index < 0;

    let newEmails;
    const contactType = validatedData.contactType ? validatedData.contactType : emails[index]?.apLabelId;
    // We want to be able to set this to null, but use previous value if undefined.
    const fieldLabel = validatedData.fieldLabel || validatedData.fieldLabel === null ?
      validatedData.fieldLabel : emails[index]?.fieldLabel;
    const fieldValue = validatedData.email ?? emails[index]?.fieldValue;

    if (isNew) {
      const newEmail = { ...validatedData, isNew: true };
      newEmails = [...emails, newEmail];
    } else {
      newEmails = [...emails];
      newEmails[index] = {
        ...newEmails[index],
        fieldValue,
        apLabelId: contactType,
        fieldLabel,
      };
    }

    updateLeadFields({
      orgId,
      leadId,
      updateData: {
        contact: {
          ...contact,
          emails: newEmails,
        },
      },
      emailUpdate: {
        emailId: isNew ? null : emails[index].id,
        fieldValue,
        fieldLabel,
        contactType,
      },
    });
    setNewEmail(false);
  };

  const handleEmailFieldValueUpdate = (e, id) => {
    const newEmail = e.target.value;
    const index = emails.findIndex(email => id === email.id);
    const isNew = index < 0;
    const currentEmailValue = isNew ? null : emails[index].fieldValue;

    // Validate email
    const { validatedData, invalidFields } = validateLeadUpdate(
      { email: newEmail },
      { email: currentEmailValue },
    );

    if (invalidFields.includes('email')) {
      e.target.value = emails[index]?.fieldValue;
      return;
    }
    handleEmailUpdate(index, validatedData);
  };

  /*
  const handleEmailTypeUpdate = (id, { contactType, fieldLabel }) => {
    if (!id) return;

    const index = emails.findIndex(email => id === email.id);

    const newType = {
      fieldLabel: contactType === 'custom' ? fieldLabel : null,
      contactType,
    };

    // #TODO: might want to handle error.
    handleEmailUpdate(index, newType);
  };
  */

  const handlePhoneUpdate = (index, validatedData) => {
    const isNew = index < 0;

    let newPhones;
    const contactType = validatedData.contactType ? validatedData.contactType : phones[index]?.apLabelId;
    // We want to be able to set this to null, but use previous value if undefined.
    const fieldLabel = validatedData.fieldLabel || validatedData.fieldLabel === null ?
    validatedData.fieldLabel : phones[index]?.fieldLabel;
    const fieldValue = validatedData.phone ?? phones[index]?.fieldValue;

    if (isNew) {
      const newPhone = { ...validatedData, isNew: true };
      newPhones = [...phones, newPhone];
    } else {
      newPhones = [...phones];
      newPhones[index] = {
        ...newPhones[index],
        fieldValue,
        apLabelId: contactType,
        fieldLabel,
      };
    }

    updateLeadFields({
      orgId,
      leadId,
      updateData: {
        contact: {
          ...contact,
          phones: newPhones,
        },
      },
      phoneUpdate: {
        phoneId: isNew ? null : phones[index].id,
        fieldValue,
        fieldLabel,
        contactType,
      },
    });
    setNewPhone(false);
  };

  const handlePhoneFieldValueUpdate = (e, id) => {
    const newPhone = e.target.value;
    const index = phones.findIndex(phone => id === phone.id);
    const isNew = index < 0;
    const currentPhoneValue = isNew ? null : phones[index].fieldValue;

    // Validate phone
    const { validatedData, invalidFields } = validateLeadUpdate(
      { phone: newPhone },
      { phone: currentPhoneValue },
    );

    if (invalidFields.includes('phone')) {
      e.target.value = phones[index];
      return;
    }

    handlePhoneUpdate(index, validatedData);
  };

  /*
  const handlePhoneTypeUpdate = (id, { contactType, fieldLabel }) => {
    if (!id) return;

    const index = phones.findIndex(website => id === website.id);

    const newType = {
      fieldLabel: contactType === 'custom' ? fieldLabel : null,
      contactType,
    };

    // #TODO: might want to handle error.
    handlePhoneUpdate(index, newType);
  };
  */

  const handleAddressUpdate = (index, field, validatedData) => {
    const isNew = index < 0;
    const address = isNew ? {} : addresses[index];

    let newAddresses;
    const contactType = validatedData.contactType ? validatedData.contactType : addresses[index]?.apLabelId;
    // We want to be able to set this to null, but use previous value if undefined.
    const fieldLabel = validatedData.fieldLabel || validatedData.fieldLabel === null ?
      validatedData.fieldLabel : addresses[index]?.fieldLabel;
    const fieldValue = validatedData[field] ?? addresses[index]?.[field];

    if (isNew) {
      const newAddress = { ...validatedData, isNew: true };
      newAddresses = [...addresses, newAddress];
    } else {
      const newAddress = {
        ...addresses[index],
        apLabelId: contactType,
        fieldLabel,
        ... field && { [field]: fieldValue },
      };
      newAddresses = [...addresses];
      newAddresses[index] = newAddress;
    }

    updateLeadFields({
      orgId,
      leadId,
      updateData: {
        contact: {
          ...contact,
          addresses: newAddresses,
        },
      },
      addressUpdate: {
        ...address,
        addressId: isNew ? null : address.id,
        ... field && { [field]: fieldValue },
        contactType,
        fieldLabel,
      },
    });
    setNewAddress(false);
  };

  const handleAddressFieldValueUpdate = (field, value, target, id) => {
    const index = addresses.findIndex(address => id === address.id);
    const isNew = index < 0;
    const address = isNew ? {} : addresses[index];

    if (!isNew && value === addresses[index][field]) return;

    // Validate address
    const { validatedData, invalidFields } = validateLeadUpdate(
      { [field]: value },
      { [field]: address?.[field] },
      { serviceTypes, leadSources: sourceTypes },
    );

    if (invalidFields.includes(field)) {
      target.value = address?.[field] || '';
      return;
    }
    handleAddressUpdate(index, field, validatedData);
  };

  /*
  const handleAddressTypeUpdate = (id, { contactType, fieldLabel }) => {
    if (!id) return;

    const index = addresses.findIndex(website => id === website.id);

    const newType = {
      fieldLabel: contactType === 'custom' ? fieldLabel : null,
      contactType,
    };

    // #TODO: might want to handle error.
    handleAddressUpdate(index, null, newType);
  };
  */

  const handleWebsiteUpdate = (index, validatedData) => {
    const isNew = index < 0;

    let newWebsites;
    const contactType = validatedData.contactType ? validatedData.contactType : websites[index]?.apLabelId;
    // We want to be able to set this to null, but use previous value if undefined.
    const fieldLabel = validatedData.fieldLabel || validatedData.fieldLabel === null ?
      validatedData.fieldLabel : websites[index]?.fieldLabel;
    const fieldValue = validatedData.website ?? websites[index]?.fieldValue;

    if (isNew) {
      const newWebsite = { ...validatedData, isNew: true };
      newWebsites = [...websites, newWebsite];
    } else {
      newWebsites = [...websites];
      newWebsites[index] = {
        ...newWebsites[index],
        fieldValue,
        apLabelId: contactType,
        fieldLabel,
      };
    }

    updateLeadFields({
      orgId,
      leadId,
      updateData: {
        contact: {
          ...contact,
          websites: newWebsites,
        },
      },
      websiteUpdate: {
        websiteId: isNew ? null : websites[index].id,
        fieldValue,
        fieldLabel,
        contactType,
      },
    });
    setNewWebsite(false);
  };

  const handleWebsiteFieldValueUpdate = (e, id) => {
    const newWebsite = e.target.value;
    const index = websites.findIndex(website => id === website.id);
    const isNew = index < 0;
    const currentWebsiteValue = isNew ? null : websites[index].fieldValue;

    if (!isNew && newWebsite === websites[index].fieldValue) return;

    // validate website
    const { validatedData, invalidFields } = validateLeadUpdate(
      { website: newWebsite },
      { website: currentWebsiteValue },
    );

    if (invalidFields.includes('website')) {
      e.target.value = websites[index].fieldValue;
      return;
    }

    handleWebsiteUpdate(index, validatedData);
  };

  /*
  const handleWebsiteTypeUpdate = (id, { contactType, fieldLabel }) => {
    if (!id) return;

    const index = websites.findIndex(website => id === website.id);

    const newType = {
      fieldLabel: contactType === 'custom' ? fieldLabel : null,
      contactType,
    };

    // #TODO: might want to handle error.
    handleWebsiteUpdate(index, newType);
  };
  */

  const handleDeleteLeadContactData = (contactId, type) => {
    dispatch(deleteLeadContactData(orgId, leadId, contactId, type));
  };

  const handleAddEmail = () => {
    setNewEmail(true);
  };

  const handleAddAddress = () => {
    setNewAddress(true);
  };

  const handleAddPhone = () => {
    setNewPhone(true);
  };

  const handleAddWebsite = () => {
    setNewWebsite(true);
  };

  return (
    <>
      <div className="lead-sidebar__row">
        <div className="lead-sidebar__label">Company</div>
        <InputField
          name="company"
          placeholder="Company"
          showLabel={false}
          defaultValue={contactCompanyName}
          onBlur={(e) => {
            const newCompany = e.target.value.trim();
            if (newCompany !== contactCompanyName) {
              const { validatedData, invalidFields } = validateLeadUpdate(
                { contactCompanyName: newCompany || '' },
                null,
                { serviceTypes, leadSources: sourceTypes },
              );
              if (invalidFields.includes('contactCompanyName')) {
                e.target.value = contactCompanyName;
              } else {
                updateLeadFields({ orgId, leadId, updateData: { lead: validatedData } });
              }
            }
          }}
        />
      </div>
      <div className="lead-sidebar__row">
        <div className="lead-sidebar__label">Title</div>
        <InputField
          name="role"
          placeholder="Title"
          showLabel={false}
          defaultValue={role}
          onBlur={(e) => {
            const newRole = e.target.value.trim();
            if (newRole !== role) {
              const { validatedData, invalidFields } = validateLeadUpdate(
                { role: newRole || '' },
                null,
                { serviceTypes, leadSources: sourceTypes },
              );
              if (invalidFields.includes('role')) {
                e.target.value = role;
              } else {
                updateLeadFields({ orgId, leadId, updateData: { lead: validatedData } });
              }
            }
          }}
        />
      </div>
      <div className="lead-sidebar__row">
        <div className="lead-sidebar__label">Website</div>
        <ContactField
          name="website"
          placeholder="Website"
          type="website"
          typeValue={
            primaryWebsite?.apLabelId === 'custom' ?
              primaryWebsite?.fieldLabel : primaryWebsite?.apLabelId
          }
          value={primaryWebsite?.fieldValue}
          onBlur={(e) => handleWebsiteFieldValueUpdate(e, primaryWebsite?.id)}
        />
      </div>
      <AddressContactFields
        address={primaryAddress}
        onAddressUpdate={handleAddressFieldValueUpdate}
      />
      <div className="lead-sidebar__additional-contacts">
        {(hasAddtionalContact || isCreating) && (
          <>
            <div className="accordion__title">
              Additional contact info
            </div>
            {restEmails.map((email, idx) => {
              const labelInfo = CONTACT_FIELD_LABEL_INFO.email;
              const typeValue = email.apLabelId === 'custom' ? email.fieldLabel : email.apLabelId;
              const typeLabel = email.apLabelId !== 'custom' ?
                labelInfo.find(info => info.id === email.apLabelId)?.label || '' :
                email.fieldLabel;
              const title = typeLabel ? (
                <>Email <span className="contact-info__type">({typeLabel})</span></>
              ) : 'Email';
              return (
                <div className="lead-sidebar__row lead-sidebar__row--contact-info">
                  <div className="lead-sidebar__label">{title}</div>
                  <div className="lead-sidebar__value lead-sidebar__value--full">
                    <ContactField
                      key={`additional-email-${idx}`}
                      name={`additional email ${idx + 1}`}
                      type="email"
                      value={email.fieldValue}
                      typeValue={typeValue}
                      placeholder="Email"
                      onBlur={(e) => handleEmailFieldValueUpdate(e, email.id)}
                      onDelete={() => handleDeleteLeadContactData(email.id, 'emails')}
                    />
                  </div>
                </div>
              );
            })}
            {createNewEmail && (
              <div className="lead-sidebar__row">
                <div className="lead-sidebar__label">Email</div>
                <div className="lead-sidebar__value lead-sidebar__value--full">
                  <InputField
                    autoFocus
                    key="new-additional-email"
                    name="new additional email"
                    showLabel={false}
                    placeholder="New email"
                    onBlur={(e) => handleEmailFieldValueUpdate(e)}
                  />
                </div>
              </div>
            )}
            {restPhones.map((phone, idx) => {
              const labelInfo = CONTACT_FIELD_LABEL_INFO.phone;
              const typeValue = phone.apLabelId === 'custom' ? phone.fieldLabel : phone.apLabelId;
              const typeLabel = phone.apLabelId !== 'custom' ?
                labelInfo.find(info => info.id === phone.apLabelId)?.label || '' :
                phone.fieldLabel;
              const title = typeLabel ? (
                <>Phone <span className="contact-info__type">({typeLabel})</span></>
              ) : 'Phone';
              return (
                <div className="lead-sidebar__row lead-sidebar__row--contact-info">
                  <div className="lead-sidebar__label">{title}</div>
                  <div className="lead-sidebar__value lead-sidebar__value--full">
                    <ContactField
                      key={`additional-phone-${idx}`}
                      name={`additional-phone-${idx}`}
                      type="phone"
                      value={phone.fieldValue}
                      typeValue={typeValue}
                      placeholder="Phone number"
                      onBlur={(e) => handlePhoneFieldValueUpdate(e, phone.id)}
                      onDelete={() => handleDeleteLeadContactData(phone.id, 'websites')}
                    />
                  </div>
                </div>
              );
            })}
            {createNewPhone && (
              <div className="lead-sidebar__row">
                <div className="lead-sidebar__label">Phone</div>
                <div className="lead-sidebar__value lead-sidebar__value--full">
                  <InputField
                    autoFocus
                    key="new-additional-phone"
                    name="new-additional-phone"
                    showLabel={false}
                    placeholder="New phone number"
                    onBlur={(e) => handlePhoneFieldValueUpdate(e)}
                  />
                </div>
              </div>
            )}
            {addtWebsites.map((website, idx) => {
              const labelInfo = CONTACT_FIELD_LABEL_INFO.website;
              const typeValue = website.apLabelId === 'custom' ? website.fieldLabel : website.apLabelId;
              const typeLabel = website.apLabelId !== 'custom' ?
                labelInfo.find(info => info.id === website.apLabelId)?.label || '' :
                website.fieldLabel;
              const title = typeLabel ? (
                <>Website <span className="contact-info__type">({typeLabel})</span></>
              ) : 'Website';
              return (
                <div className="lead-sidebar__row lead-sidebar__row--contact-info">
                  <div className="lead-sidebar__label">{title}</div>
                  <div className="lead-sidebar__value lead-sidebar__value--full">
                    <ContactField
                      key={`additional-website-${idx}`}
                      name={`additional-website-${idx}`}
                      value={website.fieldValue}
                      typeValue={typeValue}
                      placeholder="Website"
                      onBlur={(e) => handleWebsiteFieldValueUpdate(e, website.id)}
                      onDelete={() => handleDeleteLeadContactData(website.id, 'websites')}
                      type="website"
                    />
                  </div>
                </div>
              );
            })}
            {createNewWebsite && (
              <div className="lead-sidebar__row">
                <div className="lead-sidebar__label">Website</div>
                <div className="lead-sidebar__value lead-sidebar__value--full">
                  <InputField
                    autoFocus
                    key="new-additional-website"
                    name="new-additional-website"
                    showLabel={false}
                    placeholder="New website"
                    onBlur={(e) => handleWebsiteFieldValueUpdate(e)}
                  />
                </div>
              </div>
            )}
            {addtAddresses.map(address => {
              const labelInfo = CONTACT_FIELD_LABEL_INFO.address;
              const typeValue = address.apLabelId === 'custom' ? address.fieldLabel : address.apLabelId;
              const typeLabel = address.apLabelId !== 'custom' ?
                labelInfo.find(info => info.id === address.apLabelId)?.label || '' :
                address.fieldLabel;
              return (
                <>
                  {typeLabel && (
                    <>
                      <div className="lead-sidebar__label">
                        Address
                        &nbsp;<span className="contact-info__type">({typeLabel})</span>
                      </div>
                    </>
                  )}
                  <AddressContactFields
                    key={`additional-address-${address.id}`}
                    address={address}
                    onAddressUpdate={handleAddressFieldValueUpdate}
                    onDelete={handleDeleteLeadContactData}
                    typeValue={typeValue}
                  />
                </>
              );
            })}
            {createNewAddress && (
              <AddressContactFields
                address={{}}
                autoFocus
                onAddressUpdate={handleAddressFieldValueUpdate}
              />
            )}
          </>
        )}
        <LeadRecordAdditonalContact
          addPhone={handleAddPhone}
          addEmail={handleAddEmail}
          addAddress={handleAddAddress}
          addWebsite={handleAddWebsite}
        />
      </div>
    </>
  );
};

export default LeadRecordContactInfo;
